import { type RefObject, useCallback, useLayoutEffect, useState } from 'react'

type Caret = { x: number; y: number }

/**
 * The composer's drawn caret (the system's, not the browser's). A hidden
 * mirror of the text carries a marker at the selection; its offset locates the
 * caret. The hook only measures: position reaches CSS through the two
 * `--local-caret-*` custom properties the component sets, and nothing here
 * writes a DOM style.
 */
export function useDrawnCaret({
  fieldRef,
  wrapRef,
  markerRef,
  value,
  disabled,
}: {
  fieldRef: RefObject<HTMLTextAreaElement | null>
  wrapRef: RefObject<HTMLDivElement | null>
  markerRef: RefObject<HTMLSpanElement | null>
  value: string
  disabled: boolean
}) {
  const [focused, setFocused] = useState(false)
  // Both ends: growing a selection from the caret, or collapsing it back to
  // the anchor, leaves the start unchanged but must hide or re-place the caret.
  const [range, setRange] = useState<[number, number]>([0, 0])
  const selection = range[0]
  const [caret, setCaret] = useState<Caret | null>(null)

  const measure = useCallback((): Caret | null => {
    const field = fieldRef.current
    const wrap = wrapRef.current
    const marker = markerRef.current
    if (!field || !wrap || !marker) return null
    if (field.selectionStart !== field.selectionEnd) return null
    const style = getComputedStyle(field)
    const lineHeight = Number.parseFloat(style.lineHeight) || 0
    const rtl = style.direction === 'rtl'
    // The marker is an empty inline span, so it adds no break opportunity and
    // the mirror wraps exactly like the field. Its box is the font's content
    // area, centred on the line; the line's top is half a line above centre.
    const box = marker.getClientRects()[0]
    let x: number
    let top: number
    if (box) {
      const frame = wrap.getBoundingClientRect()
      top = box.top + box.height / 2 - lineHeight / 2 - frame.top
      x = rtl ? frame.right - box.right : box.left - frame.left
    } else {
      top = marker.offsetTop
      x = rtl
        ? wrap.clientWidth - marker.offsetLeft - marker.offsetWidth
        : marker.offsetLeft
    }
    const y = top - field.scrollTop
    if (y < 0 || y >= field.clientHeight - lineHeight) return null
    return { x, y }
  }, [fieldRef, wrapRef, markerRef])

  const place = useCallback(() => {
    setCaret(focused && !disabled ? measure() : null)
  }, [focused, disabled, measure])

  // A layout effect so the caret moves in the same frame as the text. A value
  // change can leave the stored selection stale; resync it first, and the
  // re-render that follows measures against the right mirror.
  // biome-ignore lint/correctness/useExhaustiveDependencies: value and the range drive the mirror
  useLayoutEffect(() => {
    const field = fieldRef.current
    if (
      field &&
      (field.selectionStart !== range[0] || field.selectionEnd !== range[1])
    ) {
      setRange([field.selectionStart, field.selectionEnd])
      return
    }
    place()
  }, [value, range, focused, disabled, fieldRef, place])

  // Wrapping, and so the caret, shifts when the column resizes.
  useLayoutEffect(() => {
    const wrap = wrapRef.current
    if (!wrap || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(place)
    observer.observe(wrap)
    return () => observer.disconnect()
  }, [wrapRef, place])

  const readRange = () => {
    const field = fieldRef.current
    if (!field) return
    const { selectionStart: start, selectionEnd: end } = field
    setRange((prev) =>
      prev[0] === start && prev[1] === end ? prev : [start, end],
    )
  }

  const handlers = {
    onFocus: () => {
      readRange()
      setFocused(true)
    },
    onBlur: () => setFocused(false),
    onSelect: readRange,
    onScroll: () => place(),
  }

  return { caret, selection, handlers }
}
